<template>
  <div>
    <!-- System Metrics Cards -->
    <div class="row">
      <!-- CPU Card -->
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-primary">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-microchip text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">CPU Usage</p>
                <p class="card-title">{{ metrics.cpu ? `${metrics.cpu.usage_percent.toFixed(1)}%` : 'Loading...' }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white" v-if="metrics.cpu">
                <i class="fas fa-server"></i> {{ metrics.cpu.cores }} cores
              </span>
            </div>
          </div>
        </card>
      </div>

      <!-- Memory Card -->
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-info">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-memory text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">Memory Usage</p>
                <p class="card-title">{{ metrics.memory ? `${metrics.memory.usage_percent.toFixed(1)}%` : 'Loading...' }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white" v-if="metrics.memory">
                <i class="fas fa-database"></i> {{ (metrics.memory.used_mb / 1024).toFixed(1) }} GB / {{ (metrics.memory.total_mb / 1024).toFixed(1) }} GB
              </span>
            </div>
          </div>
        </card>
      </div>

      <!-- Disk Card -->
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-warning">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-hdd text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">Disk Usage</p>
                <p class="card-title">{{ metrics.disk ? `${metrics.disk.usage_percent.toFixed(1)}%` : 'Loading...' }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span class="text-white" v-if="metrics.disk">
                <i class="fas fa-database"></i> {{ metrics.disk.used_gb.toFixed(1) }} GB / {{ metrics.disk.total_gb.toFixed(1) }} GB
              </span>
            </div>
          </div>
        </card>
      </div>

      <!-- Uptime Card -->
      <div class="col-lg-3 col-md-6">
        <card type="card-stats" class="gradient-success">
          <div class="row">
            <div class="col-5 col-md-4">
              <div class="icon-big text-center">
                <i class="fas fa-clock text-white"></i>
              </div>
            </div>
            <div class="col-7 col-md-8">
              <div class="numbers">
                <p class="card-category">System Uptime</p>
                <p class="card-title">{{ metrics.uptime ? formatUptime(metrics.uptime.seconds) : 'Loading...' }}</p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <hr>
            <div class="stats">
              <span
                class="text-white cursor-pointer"
                v-if="metrics.uptime"
                @click="showRebootDialog = true"
                style="cursor: pointer;"
              >
                <i class="fas fa-redo-alt"></i> Reboot System
              </span>
            </div>
          </div>
        </card>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <tracked-match-ids />
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <notes />
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <social-media-profiles />
      </div>
    </div>

    <!-- Reboot Confirmation Dialog -->
    <modal :show.sync="showRebootDialog" modal-classes="modal-dialog-centered">
      <card type="secondary" shadow header-classes="bg-danger pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <template>
          <div class="text-center mb-4">
            <i class="fas fa-exclamation-triangle text-danger" style="font-size: 48px;"></i>
            <h3 class="mt-3">Reboot System?</h3>
            <p class="text-white">
              Are you sure you want to reboot the system? This will restart all services and may cause temporary downtime.
            </p>
          </div>

          <div class="text-center">
            <base-button type="secondary" @click="showRebootDialog = false" class="mr-2">
              Cancel
            </base-button>
            <base-button type="danger" @click="confirmReboot" :disabled="isRebooting">
              <i class="fas fa-redo-alt"></i> {{ isRebooting ? 'Rebooting...' : 'Yes, Reboot' }}
            </base-button>
          </div>
        </template>
      </card>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import TrackedMatchIds from "@/components/TrackedMatchIds.vue";
import SocialMediaProfiles from "@/components/SocialMediaProfiles.vue";
import Notes from "@/components/Notes.vue";
import { Card, BaseButton, Modal } from '@/components';
import createPoller from '@/utils/createPoller';

export default {
  name: "Dashboard",
  components: {
    TrackedMatchIds,
    SocialMediaProfiles,
    Notes,
    Card,
    BaseButton,
    Modal,
  },
  data() {
    return {
      metrics: {
        cpu: null,
        memory: null,
        disk: null,
        uptime: null,
      },
      showRebootDialog: false,
      isRebooting: false,
    };
  },
  created() {
    this.metricsPoller = createPoller({
      fetchFn: () => this.fetchMetrics(),
      interval: 10000,
      immediate: true,
      onError: (error) => console.error('Error fetching system metrics:', error),
    });
  },
  mounted() {
    this.metricsPoller.start();
  },
  beforeDestroy() {
    this.metricsPoller.destroy();
  },
  methods: {
    async fetchMetrics() {
      const response = await axios.get('/system-info/metrics');
      this.metrics = response.data;
    },
    async confirmReboot() {
      this.showRebootDialog = false;
      this.isRebooting = true;

      try {
        await axios.post('/system-info/reboot');
        this.$toast.success('System reboot initiated successfully');

        // Stop polling during reboot
        this.metricsPoller.stop();

        // Show informational message
        setTimeout(() => {
          this.$toast.info('System is rebooting. This page will become unavailable temporarily.');
        }, 2000);
      } catch (error) {
        console.error('Error initiating reboot:', error);
        this.$toast.error('Failed to initiate system reboot');
        this.isRebooting = false;
      }
    },
    formatUptime(seconds) {
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${minutes}m`;
      }
    },
  },
};
</script>

<style scoped>
/* Stats Cards */
.icon-big {
  font-size: 3em;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-big i {
  line-height: 1;
}

.numbers {
  text-align: right;
  font-size: 2em;
}

.numbers .card-category {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 0;
  text-transform: uppercase;
  font-weight: 400;
}

.numbers .card-title {
  margin: 0;
  font-size: 1.7em;
  font-weight: 300;
  color: #ffffff;
}

.card-stats .card-footer {
  padding: 0.75rem 1.5rem;
  margin-top: 1rem;
}

.card-stats .card-footer hr {
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.card-stats .card-footer .stats {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
}

.card-stats .card-footer .stats i {
  margin-right: 5px;
}

/* Gradient backgrounds */
.gradient-primary {
  background: linear-gradient(to bottom left, #1d8cf8, #3358f4, #1d8cf8);
}

.gradient-info {
  background: linear-gradient(to bottom left, #1d8cf8, #1171ef, #11cdef);
}

.gradient-warning {
  background: linear-gradient(to bottom left, #ff8d72, #ff6491, #ff8d72);
}

.gradient-success {
  background: linear-gradient(to bottom left, #00bf9a, #00b09b, #00bf9a);
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}

.cursor-pointer:hover {
  opacity: 0.8;
}
</style>
